import type { UsageSettingsPayload } from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentsFetched';
import UsageSettingView from '@UI/Store/AiConfiguration/Views/UsageSettingView';

export type VocabularyBounds = {
	readonly maxTerms: number;
	readonly maxTermLength: number;
};

// No bounds carried: a prompt has no length limit, so there is nothing to regroup for it.
export type TextBounds = Record<string, never>;

export type TemperatureBounds = {
	readonly minimum: number;
	readonly maximum: number;
};

/**
 * Immutable View: the four call settings for one usage, one card. Extracts each setting's bounds from
 * the flat wire payload into its own `UsageSettingView`; `contextPrompt`/`systemPrompt` carry an empty
 * `bounds` — prompts have no length limit.
 */
class UsageSettingsView {
	readonly vocabularyTerms: UsageSettingView<readonly string[], VocabularyBounds>;
	readonly contextPrompt: UsageSettingView<string, TextBounds>;
	readonly systemPrompt: UsageSettingView<string, TextBounds>;
	readonly temperature: UsageSettingView<number, TemperatureBounds>;

	constructor(payload: UsageSettingsPayload) {
		this.vocabularyTerms = new UsageSettingView(payload.vocabularyTerms, {
			maxTerms: payload.vocabularyTerms.maxTerms,
			maxTermLength: payload.vocabularyTerms.maxTermLength,
		});
		this.contextPrompt = new UsageSettingView(payload.contextPrompt, {});
		this.systemPrompt = new UsageSettingView(payload.systemPrompt, {});
		this.temperature = new UsageSettingView(payload.temperature, {
			minimum: payload.temperature.minimum,
			maximum: payload.temperature.maximum,
		});

		Object.freeze(this);
	}

	get hasAnyConflict(): boolean {
		return (
			this.vocabularyTerms.isInConflict ||
			this.contextPrompt.isInConflict ||
			this.systemPrompt.isInConflict ||
			this.temperature.isInConflict
		);
	}
}

export default UsageSettingsView;
